/**
 * Licence changes from the state register (issue #635) — API contract.
 *
 * Mirrors `apps.licensing.comparison.StateLicenseChangeRow.as_dict()` and
 * `apps.desk.statelicense.state_license_queue()` / `state_license_summary()`.
 * The queue is the ~4 changes a day Tim used to find by hand: every member
 * whose CE21 licence dates differ from the latest imported state list. The
 * "Done in CE21" tick is a `CaseAcknowledgement` on `queue_key =
 * "state_license_change"` — the same primitive every other desk queue uses.
 */
import type { DeskReviewState } from "./desk.types";

/** The queue key the tick is recorded against (`apps.outreach.models`). */
export const STATE_LICENSE_QUEUE_KEY = "state_license_change";

/** Where the queue renders — a People view, not a page (PRODUCT.md rule 3). */
export const STATE_LICENSE_QUEUE_HREF = `/membership/people?queue=${STATE_LICENSE_QUEUE_KEY}`;

export type StateLicenseField = "issue" | "expiration";

export interface StateLicenseChangeReview {
  review_state: DeskReviewState;
  acknowledgement_id: number | null;
  reviewed_by: string | null;
  /** ISO datetime. */
  reviewed_at: string | null;
  review_note: string | null;
  resolution: string | null;
}

/** One member whose dates differ from the state's. */
export interface StateLicenseChange {
  memberId: number;
  name: string;
  email: string;
  licenseNumber: string;
  /** The state's status — context only, never compared (`Active`, `Late Renewal`, …). */
  stateStatus: string;
  /** ISO `YYYY-MM-DD` or `""`. */
  stateIssueDate: string;
  stateExpirationDate: string;
  ce21IssueDate: string;
  ce21ExpirationDate: string;
  changedFields: StateLicenseField[];
  /** `license` (exact key) or `name_city` (fallback, flagged). */
  matchMethod: string;
  lowConfidence: boolean;
  review: StateLicenseChangeReview;
}

export interface StateLicenseQueueCounts {
  /** Rows in the latest state file. */
  rows: number;
  /** Members whose dates differ (the whole queue, ticked or not). */
  changes: number;
  /** `open` + `resurfaced` — what is left to do in CE21. */
  to_do: number;
  /** Ticked "Done in CE21". */
  done: number;
  resurfaced: number;
  low_confidence: number;
  /** Licensees CE21 does not know — counted, never listed. */
  unmatched: number;
  /** Two CE21 people on one licence number — quarantined, never listed. */
  ambiguous: number;
}

export interface StateLicenseQueueResponse {
  as_of: string;
  queue_key: string;
  /** ISO datetime, or `null` when no state file has been loaded yet. */
  imported_at: string | null;
  filename: string;
  counts: StateLicenseQueueCounts;
  review_state: DeskReviewState | null;
  count: number;
  limit: number;
  offset: number;
  people: StateLicenseChange[];
}

/** `GET /api/insights/state-license-summary` — counts only (the Today row). */
export interface StateLicenseSummary {
  /** Still to do in CE21 (`open` + `resurfaced`). */
  changes: number;
  done: number;
  resurfaced: number;
  total: number;
  unmatched: number;
  ambiguous: number;
  lowConfidence: number;
  rows: number;
  importedAt: string | null;
}
